import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Send,
  Loader2,
  AlertCircle,
  FileText,
  Mail,
  MessageCircle,
  Phone /* , PenLine */,
} from "lucide-react";
import { AvailabilityDot } from "@/components/atoms/AvailabilityDot";
import { Button } from "@/components/atoms/Button";
import { Tooltip } from "@/components/atoms/Tooltip";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import { useCountries } from "@/hooks/useCountries";
import { cn } from "@/lib/cn";
import {
  showActionError,
  showActionErrorFromUnknown,
  showActionSuccess,
} from "@/lib/feedback";
import { contactPropertyListScope } from "@/lib/contactPropertyListScope";
import {
  buildReachoutResolverContext,
  templateUsesPathPrefix,
} from "@/lib/reachoutResolverContext";
import { contactsService } from "@/services/contactsService";
import { diaryService } from "@/services/diaryService";
import { propertiesService } from "@/services/propertiesService";
import { resolveReachOutHeaderImageUrl, resolveReachOutHeaderImageUrlForApi } from "@/lib/agencyLogo";
import {
  reachoutService,
  LEAD_INTENT_OPTIONS,
  type LeadIntent,
  type ReachoutChannelType,
  type ReachoutMessage,
} from "@/services/reachoutService";
import {
  whatsappTemplateService,
  countTemplateParams,
  resolveTemplateParams,
  type WhatsappTemplate,
} from "@/services/whatsappTemplateService";
import { useKeycloak } from "@/modules/auth/context/KeycloakContext";
import { useSettingsStore } from "@/store/settingsStore";
import { BranchScopeSelect } from "@/components/molecules/ScopePicker";
import { useUiStore } from "@/store/uiStore";
import { useProfileStore } from "@/store/profileStore";
import {
  findBranch,
  findLegalEntity,
} from "@/modules/subscription/billingAccess";
import type { BranchHierarchy, LegalEntityHierarchy } from "@/services/profileService";
import type { PageResponse, ApiError } from "@/types/api";
import type { ContactSummary, PropertyDetail, PropertySummary } from "@/types/entities";
import {
  defaultDiarySlotForDate,
  toDatetimeLocalValue,
  toDiaryApiRangeStart,
} from "@/utils/date-helpers";
import { contactPrimaryLabel } from "@/lib/contactDisplay";
import { resolvePhoneCodeOptionValue } from "@/modules/contacts/contactFormUtils";
import { ReachOutContactCreateModal } from "./components/ReachOutContactCreateModal";
import { WhatsappLivePreview } from "./components/WhatsappLivePreview";
import { usePropertyImagePresence } from "@/modules/properties/usePropertyImagePresence";

type MessageMode = "custom" | "template";


function normalizePhoneInput(phone: string): string {
  return phone.replace(/[\s\-().]/g, "").trim();
}
function normalizeContactSearchQuery(query: string): string {
  const trimmed = query.trim();
  if (!trimmed) return "";
  const phoneLike = /^[+\d\s\-().]+$/.test(trimmed);
  if (!phoneLike) return trimmed;
  return normalizePhoneInput(trimmed);
}
/** Digits-only representation of a phone value, for substring matching. */
function phoneDigits(phone: string | null | undefined): string {
  return (phone ?? "").replace(/\D/g, "");
}
/**
 * The contacts API returns loose/fuzzy matches for numeric searches, so a query
 * like "829637" can surface phone numbers that don't actually contain those
 * digits. When the query is phone-like, narrow the returned rows on the client
 * to those whose mobile number contains the searched digit sequence.
 */
function filterContactsByPhoneQuery(
  contacts: ContactSummary[],
  query: string,
  keepId?: string,
): ContactSummary[] {
  const trimmed = query.trim();
  if (!trimmed || !/^[+\d\s\-().]+$/.test(trimmed)) return contacts;
  const digits = phoneDigits(trimmed);
  if (!digits) return contacts;
  return contacts.filter(
    (c) =>
      (keepId != null && c.id === keepId) ||
      phoneDigits(c.mobilePhone).includes(digits),
  );
}
/** Substitutes {1}..{N} placeholders with the provided params for preview / audit. */
function renderTemplatePreview(body: string, params: string[]): string {
  return body.replace(/\{(\d+)\}/g, (_, idx) => {
    const n = Number.parseInt(idx, 10);
    const v = Number.isFinite(n) ? params[n - 1] : "";
    return v ?? "";
  });
}
/** Lightweight E.164 check — avoids format-phone when the number is already international. */
function looksLikeE164(phone: string): boolean {
  const p = normalizePhoneInput(phone);
  return /^\+[1-9]\d{7,14}$/.test(p);
}

const PROPERTY_PAGE_SIZE = 50;
const CONTACT_PAGE_SIZE = 50;

function findParentLegalEntity(
  entities: LegalEntityHierarchy[],
  childId: string,
  parent: LegalEntityHierarchy | null = null,
): LegalEntityHierarchy | null {
  for (const le of entities) {
    if (le.id === childId) return parent;
    const found = findParentLegalEntity(le.subEntities ?? [], childId, le);
    if (found) return found;
  }
  return null;
}

function hasActiveWhatsappChannel(
  node: Pick<LegalEntityHierarchy, "channelActives" | "whatsappEnabled"> | null,
): boolean {
  return !!(node?.channelActives?.WHATSAPP && node?.whatsappEnabled);
}

/** Branch → parent LE → root LE, matching backend ChannelResolverUtil. */
function hasActiveWhatsappChannelInHierarchy(
  entities: LegalEntityHierarchy[],
  branchId: string,
): boolean {
  const branch = findBranch(entities, branchId);
  if (!branch) return false;
  if (hasActiveWhatsappChannel(branch)) return true;

  let legalEntityId = branch.legalEntityId ?? null;
  while (legalEntityId) {
    const le = findLegalEntity(entities, legalEntityId);
    if (!le) break;
    if (hasActiveWhatsappChannel(le)) return true;
    legalEntityId = findParentLegalEntity(entities, legalEntityId)?.id ?? null;
  }
  return false;
}

function reachOutPropertyMarketFilter(leadIntent: LeadIntent): string | undefined {
  if (leadIntent === "AGENT_INIT_PROPERTY_RENT") return "rent";
  if (leadIntent === "AGENT_INIT_PROPERTY_SALES") return "SALES";
  return undefined;
}

const DEFAULT_REACH_OUT_TEMPLATE_NAME = "flow_start_template";

function walkLegalEntities(entities: LegalEntityHierarchy[]): LegalEntityHierarchy[] {
  return entities.flatMap((le) => [le, ...walkLegalEntities(le.subEntities ?? [])]);
}

/** All LE nodes in the organisation that contains {@link legalEntityId}. */
function orgLegalEntities(
  entities: LegalEntityHierarchy[],
  legalEntityId: string,
): LegalEntityHierarchy[] {
  for (const root of entities) {
    const flat = walkLegalEntities([root]);
    if (flat.some((le) => le.id === legalEntityId)) return flat;
  }
  const selected = findLegalEntity(entities, legalEntityId);
  return selected ? [selected] : [];
}

function pickPrimaryOrFirstBranch(branches: BranchHierarchy[] | undefined): BranchHierarchy | null {
  if (!branches?.length) return null;
  return branches.find((b) => b.isPrimary) ?? branches[0];
}

/** Top-bar branch pin, then selected LE, then any branch under that org (e.g. on a sub-LE). */
function resolveDefaultReachOutBranch(
  entities: LegalEntityHierarchy[],
  legalEntityId: string,
  pinnedBranchId?: string | null,
): BranchHierarchy | null {
  if (!legalEntityId) return null;
  if (pinnedBranchId) {
    const pinned = findBranch(entities, pinnedBranchId);
    if (pinned) return pinned;
  }

  for (const le of orgLegalEntities(entities, legalEntityId)) {
    const branch = pickPrimaryOrFirstBranch(le.branches);
    if (branch) return branch;
  }
  return null;
}

/** Infer next page when API pagination metadata is incomplete (common with market filters). */
function pageHasNext(
  res: Pick<PageResponse<unknown>, "hasNext" | "page" | "totalPages" | "totalElements" | "content">,
  pageSize: number,
): boolean {
  const contentLen = res.content?.length ?? 0;
  const page = res.page ?? 0;
  if (contentLen >= pageSize) return true;
  if (typeof res.hasNext === "boolean") return res.hasNext;
  if (Number.isFinite(res.totalElements)) {
    return page * pageSize + contentLen < res.totalElements;
  }
  if (Number.isFinite(res.totalPages)) {
    return page + 1 < res.totalPages;
  }
  return false;
}

const REACH_OUT_CHANNELS: {
  value: ReachoutChannelType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  disabled?: boolean;
  comingSoon?: boolean;
}[] = [
    { value: "WHATSAPP", label: "WhatsApp", icon: MessageCircle },
    { value: "EMAIL", label: "Email", icon: Mail, disabled: true, comingSoon: true },
    { value: "SMS", label: "SMS", icon: Phone, disabled: true, comingSoon: true },
  ];

function ReachOutChannelToggle({
  value,
  onChange,
  disabled = false,
}: {
  value: ReachoutChannelType;
  onChange: (value: ReachoutChannelType) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col sm:flex-row gap-3" role="radiogroup" aria-label="Channel">
      {REACH_OUT_CHANNELS.map(({ value: channelValue, label, icon: Icon, disabled: channelDisabled, comingSoon }) => {
        const selected = value === channelValue;
        const isDisabled = disabled || channelDisabled;
        return (
          <button
            key={channelValue}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-disabled={isDisabled}
            disabled={isDisabled}
            onClick={() => onChange(channelValue)}
            className={cn(
              "relative flex-1 flex items-center justify-center gap-2 min-h-[44px] px-3 rounded-xl border text-sm font-semibold transition-all",
              selected
                ? "bg-bridge-accent/10 border-bridge-accent text-bridge-accent shadow-sm"
                : "bg-white border-bridge-border text-bridge-fg hover:border-bridge-accent/35",
              isDisabled &&
              !comingSoon &&
              "opacity-50 cursor-not-allowed hover:border-bridge-border",
              isDisabled &&
              comingSoon &&
              "cursor-not-allowed bg-bridge-bg border-bridge-border text-bridge-muted hover:border-bridge-border",
            )}
          >
            <Icon
              className={cn(
                "w-3.5 h-3.5 shrink-0",
                selected ? "text-bridge-accent" : "text-bridge-muted",
                comingSoon && "text-bridge-subtle",
              )}
            />
            <span>{label}</span>
            {comingSoon ? (
              <span className="text-[7px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-200/80 text-slate-500">
                Coming soon
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export const ReachOutPage: React.FC = () => {
  // Legal entity scope comes from the global top-bar selector. Branch, contact,
  // property, and template scope are chosen on this form.
  const { keycloak } = useKeycloak();
  const callerEmail = keycloak?.tokenParsed?.email as string | undefined;
  const { legalEntityId, legalEntityName, branchId: contextBranchId } = useSettingsStore();
  const { profileData, isLoading: profileLoading } = useProfileStore();
  const { startPageLoading, stopPageLoading } = useUiStore();
  const { phoneCodeOptions, loading: loadingPhoneCodes } = useCountries();
  const toDialCode = (v: string) => v.split("|")[0];
  const toCompositeCode = (dialCode: string) =>
    resolvePhoneCodeOptionValue(dialCode, "", phoneCodeOptions) || dialCode;

  const [contacts, setContacts] = useState<ContactSummary[]>([]);
  const [properties, setProperties] = useState<PropertySummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (loading) startPageLoading();
    else stopPageLoading();
    return () => stopPageLoading();
  }, [loading, startPageLoading, stopPageLoading]);

  // Profile hierarchy (channelActives / whatsappEnabled) drives the WhatsApp warning.
  // Integrations updates channels separately — refresh on each visit so Reach Out
  // reflects Save / Enable Conversations without a full reload.
  useEffect(() => {
    if (!callerEmail) return;
    void useProfileStore.getState().refreshProfile(callerEmail, legalEntityId ?? undefined);
  }, [callerEmail, legalEntityId]);

  const [loadingProperties, setLoadingProperties] = useState(false);
  const [propertiesPage, setPropertiesPage] = useState(0);
  const [propertiesHasNext, setPropertiesHasNext] = useState(false);
  const propertiesFetchGeneration = useRef(0);

  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactsPage, setContactsPage] = useState(0);
  const [contactsHasNext, setContactsHasNext] = useState(false);
  const contactsFetchGeneration = useRef(0);

  const [contactSearchQuery, setContactSearchQuery] = useState("");
  const [propertySearchQuery, setPropertySearchQuery] = useState("");
  const propertySearchQueryRef = useRef("");
  propertySearchQueryRef.current = propertySearchQuery;
  const contactSearchQueryRef = useRef("");
  contactSearchQueryRef.current = contactSearchQuery;

  const [isCreateContactModalOpen, setIsCreateContactModalOpen] =
    useState(false);

  // Form state — all configurable from the UI.
  const [channelType, setChannelType] =
    useState<ReachoutChannelType>("WHATSAPP");
  const [reachOutBranchId, setReachOutBranchId] = useState("");
  const reachOutBranchIdRef = useRef(reachOutBranchId);
  reachOutBranchIdRef.current = reachOutBranchId;
  /** Kept alongside the id: the picker hands back the branch, so no lookup is needed. */
  const [reachOutBranchName, setReachOutBranchName] = useState("");
  /** Same reason as the name — the header image falls back to the branch logo. */
  const [reachOutBranchLogoUrl, setReachOutBranchLogoUrl] = useState<string | undefined>(undefined);
  const branchManuallySelectedRef = useRef(false);
  const [contactId, setContactId] = useState<string>("");
  const contactIdRef = useRef(contactId);
  contactIdRef.current = contactId;
  const contactsPageRef = useRef(contactsPage);
  const contactsHasNextRef = useRef(contactsHasNext);
  const loadingContactsRef = useRef(loadingContacts);
  contactsPageRef.current = contactsPage;
  contactsHasNextRef.current = contactsHasNext;
  loadingContactsRef.current = loadingContacts;
  const [overrideCountryCode, setOverrideCountryCode] = useState<string>("+44");
  const [propertyId, setPropertyId] = useState<string>("");
  const propertyIdRef = useRef(propertyId);
  const propertiesPageRef = useRef(propertiesPage);
  const propertiesHasNextRef = useRef(propertiesHasNext);
  const loadingPropertiesRef = useRef(loadingProperties);
  propertyIdRef.current = propertyId;
  propertiesPageRef.current = propertiesPage;
  propertiesHasNextRef.current = propertiesHasNext;
  loadingPropertiesRef.current = loadingProperties;
  const [leadIntent, setLeadIntent] = useState<LeadIntent>(
    "AGENT_INIT_PROPERTY_SALES",
  );

  // Message mode + state. Template mode picks a row from whatsapp_templates for
  // the current scope and lets the operator fill in the {1}..{N} placeholders.
  // Custom plain-text mode is disabled for now — template only.
  const [messageMode, setMessageMode] = useState<MessageMode>("template");
  // const [content, setContent] = useState<string>("");
  const [templates, setTemplates] = useState<WhatsappTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [templateParams, setTemplateParams] = useState<string[]>([]);
  const [selectedPropertyDetail, setSelectedPropertyDetail] =
    useState<PropertyDetail | null>(null);
  const [appointmentLocal, setAppointmentLocal] = useState<string>("");

  // Backend-resolved phone info
  const [phoneInfo, setPhoneInfo] = useState<{
    hasCountryCode: boolean;
    formattedPhone: string | null;
    isValid: boolean;
  } | null>(null);
  const [phoneInfoLoading, setPhoneInfoLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const showReachOutValidationError = (code: string, message: string) => {
    showActionError(message, {
      code,
      title: "Could not publish reach-out",
    });
  };

  const listScope = useMemo(
    () => contactPropertyListScope(legalEntityId, reachOutBranchId),
    [legalEntityId, reachOutBranchId],
  );

  const showNoCommunicationChannelMessage =
    channelType === "WHATSAPP" &&
    !!legalEntityId &&
    !!reachOutBranchId.trim() &&
    !hasActiveWhatsappChannelInHierarchy(
      profileData?.legalEntities ?? [],
      reachOutBranchId,
    );

  const applyDefaultReachOutBranch = useCallback(() => {
    if (!legalEntityId) {
      setReachOutBranchId("");
      setReachOutBranchName("");
      setReachOutBranchLogoUrl(undefined);
      return;
    }

    if (profileLoading || !profileData?.legalEntities?.length) return;

    const branch = resolveDefaultReachOutBranch(
      profileData.legalEntities,
      legalEntityId,
      contextBranchId,
    );
    if (!branch) {
      setReachOutBranchId("");
      setReachOutBranchName("");
      setReachOutBranchLogoUrl(undefined);
      return;
    }

    setReachOutBranchId(branch.id);
    setReachOutBranchName(branch.name ?? "");
    setReachOutBranchLogoUrl(branch.logoUrl ?? undefined);
  }, [legalEntityId, contextBranchId, profileData?.legalEntities, profileLoading]);

  // Top-bar LE changes scope — clear dependent selections so dropdowns never show stale UUIDs.
  useEffect(() => {
    branchManuallySelectedRef.current = false;
    setContactId("");
    setPropertyId("");
    setSelectedTemplateId("");
    setTemplateParams([]);
    setContactSearchQuery("");
    setPropertySearchQuery("");
    setPhoneInfo(null);
    setSelectedPropertyDetail(null);
    setAppointmentLocal("");
    setSubmitAttempted(false);
    propertiesFetchGeneration.current += 1;
    contactsFetchGeneration.current += 1;
    setContacts([]);
    setContactsPage(0);
    setContactsHasNext(false);
    contactsPageRef.current = 0;
    contactsHasNextRef.current = false;
  }, [legalEntityId]);

  // Default branch from profile until the user picks one manually.
  useEffect(() => {
    if (branchManuallySelectedRef.current) return;
    applyDefaultReachOutBranch();
  }, [applyDefaultReachOutBranch]);

  // Branch scope change — reset dependent selections without clearing branch itself.
  useEffect(() => {
    setContactId("");
    setPropertyId("");
    setSelectedTemplateId("");
    setTemplateParams([]);
    setContactSearchQuery("");
    setPropertySearchQuery("");
    setPhoneInfo(null);
    setSelectedPropertyDetail(null);
    setAppointmentLocal("");
    propertiesFetchGeneration.current += 1;
    contactsFetchGeneration.current += 1;
  }, [reachOutBranchId]);

  const fetchContacts = useCallback(
    (trimmedSearch: string, page = 0, append = false) => {
      if (!legalEntityId) return;

      const gen = ++contactsFetchGeneration.current;
      const q = normalizeContactSearchQuery(trimmedSearch);

      if (page === 0 && !append) {
        setLoadingContacts(true);
        loadingContactsRef.current = true;
      } else if (page > 0) {
        setLoadingContacts(true);
        loadingContactsRef.current = true;
      }

      contactsService
        .list(
          { ...listScope, ...(q ? { search: q } : {}) },
          { page, size: CONTACT_PAGE_SIZE },
        )
        .then((res) => {
          if (gen !== contactsFetchGeneration.current) return;
          const content = res.content ?? [];
          const nextPage = res.page ?? page;
          const hasNext = pageHasNext(res, CONTACT_PAGE_SIZE);
          setContactsPage(nextPage);
          setContactsHasNext(hasNext);
          contactsPageRef.current = nextPage;
          contactsHasNextRef.current = hasNext;
          setContacts((prev) => {
            const base = append ? [...prev, ...content] : [...content];
            const selectedId = contactIdRef.current;
            if (selectedId && !base.some((c) => c.id === selectedId)) {
              const existing = prev.find((c) => c.id === selectedId);
              if (existing) base.unshift(existing);
            }
            return base;
          });
        })
        .catch((e) => {
          if (e?.code !== "ERR_CANCELED" && e?.name !== "CanceledError") console.error(e);
        })
        .finally(() => {
          if (gen !== contactsFetchGeneration.current) return;
          setLoading(false);
          setLoadingContacts(false);
          loadingContactsRef.current = false;
        });
    },
    [legalEntityId, listScope],
  );

  useEffect(() => {
    if (!legalEntityId) {
      setLoading(false);
      return;
    }
    fetchContacts(contactSearchQuery, 0, false);
  }, [contactSearchQuery, legalEntityId, listScope, fetchContacts]);

  const handleContactEndReached = useCallback(() => {
    if (loadingContactsRef.current || !contactsHasNextRef.current) return;
    fetchContacts(contactSearchQueryRef.current, contactsPageRef.current + 1, true);
  }, [fetchContacts]);

  const fetchProperties = useCallback(
    (trimmedSearch: string, page = 0, append = false) => {
      if (!legalEntityId) return;
      if (leadIntent === "AGENT_INIT_PROPERTY_RECOMMEND") {
        setProperties([]);
        setPropertiesHasNext(false);
        setPropertiesPage(0);
        propertiesHasNextRef.current = false;
        propertiesPageRef.current = 0;
        return;
      }

      const gen = ++propertiesFetchGeneration.current;
      const q = trimmedSearch.trim();
      const marketFilter = reachOutPropertyMarketFilter(leadIntent);
      if (page > 0) {
        setLoadingProperties(true);
        loadingPropertiesRef.current = true;
      }

      propertiesService
        .list(
          {
            ...listScope,
            ...(q ? { search: q } : {}),
            market: marketFilter,
          },
          { page, size: PROPERTY_PAGE_SIZE },
        )
        .then((res) => {
          if (gen !== propertiesFetchGeneration.current) return;
          const content = res.content ?? [];
          const nextPage = res.page ?? page;
          const hasNext = pageHasNext(res, PROPERTY_PAGE_SIZE);
          setPropertiesPage(nextPage);
          setPropertiesHasNext(hasNext);
          propertiesPageRef.current = nextPage;
          propertiesHasNextRef.current = hasNext;
          setProperties((prev) => {
            const base = append ? [...prev, ...content] : [...content];
            const selectedId = propertyIdRef.current;
            if (selectedId && !base.some((p) => p.id === selectedId)) {
              const existing = prev.find((p) => p.id === selectedId);
              if (existing) base.unshift(existing);
            }
            return base;
          });
        })
        .catch(console.error)
        .finally(() => {
          if (gen !== propertiesFetchGeneration.current) return;
          setLoadingProperties(false);
          loadingPropertiesRef.current = false;
        });
    },
    [legalEntityId, listScope, leadIntent],
  );

  useEffect(() => {
    if (!legalEntityId) return;
    fetchProperties(propertySearchQuery, 0, false);
  }, [propertySearchQuery, leadIntent, legalEntityId, listScope, fetchProperties]);

  const handlePropertyEndReached = useCallback(() => {
    if (loadingPropertiesRef.current || !propertiesHasNextRef.current) return;
    fetchProperties(propertySearchQueryRef.current, propertiesPageRef.current + 1, true);
  }, [fetchProperties]);

  // Load WhatsApp templates whenever the scope changes. Templates are only
  // meaningful for WHATSAPP — for SMS/EMAIL the toggle is disabled below.
  useEffect(() => {
    if (!legalEntityId || channelType !== "WHATSAPP") {
      setTemplates([]);
      setSelectedTemplateId("");
      setTemplateParams([]);
      return;
    }
    setTemplatesLoading(true);
    whatsappTemplateService
      .list(legalEntityId, reachOutBranchId || undefined)
      .then((loaded) => {
        setTemplates(loaded);
        setSelectedTemplateId((current) => {
          if (current && loaded.some((template) => template.id === current)) {
            return current;
          }
          const defaultTemplate = loaded.find(
            (template) =>
              template.templateName?.toLowerCase() ===
              DEFAULT_REACH_OUT_TEMPLATE_NAME.toLowerCase(),
          );
          return defaultTemplate?.id ?? loaded[0]?.id ?? "";
        });
      })
      .catch(() => {
        setTemplates([]);
        setSelectedTemplateId("");
      })
      .finally(() => setTemplatesLoading(false));
  }, [legalEntityId, reachOutBranchId, channelType]);

  const selectedContact = useMemo(
    () => contacts.find((c) => c.id === contactId) ?? null,
    [contacts, contactId],
  );

  // Numbers stored without "+" get UK (+44) unless the user picks another code.
  useEffect(() => {
    if (!selectedContact?.mobilePhone) return;
    if (!normalizePhoneInput(selectedContact.mobilePhone).startsWith("+")) {
      setOverrideCountryCode("+44");
    }
  }, [selectedContact?.id, selectedContact?.mobilePhone, channelType]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) ?? null,
    [templates, selectedTemplateId],
  );

  const selectedPropertySummary = useMemo(
    () => properties.find((p) => p.id === propertyId) ?? null,
    [properties, propertyId],
  );

  const previewPropertyRows = useMemo(
    () => (selectedPropertySummary ? [selectedPropertySummary] : []),
    [selectedPropertySummary],
  );
  const { imageUrlsByPropertyId: previewImageUrlsByPropertyId } =
    usePropertyImagePresence(previewPropertyRows);

  const templateNeedsAppointment = useMemo(
    () => templateUsesPathPrefix(selectedTemplate, "appointment."),
    [selectedTemplate],
  );

  useEffect(() => {
    if (!propertyId) {
      setSelectedPropertyDetail(null);
      return;
    }

    let cancelled = false;
    propertiesService
      .getOne(propertyId)
      .then((detail) => {
        if (!cancelled) setSelectedPropertyDetail(detail);
      })
      .catch(() => {
        if (!cancelled) setSelectedPropertyDetail(null);
      });

    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  useEffect(() => {
    if (!templateNeedsAppointment) {
      setAppointmentLocal("");
      return;
    }

    const setDefaultAppointment = () => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setAppointmentLocal(defaultDiarySlotForDate(tomorrow).start);
    };

    if (!contactId && !propertyId) {
      setDefaultAppointment();
      return;
    }

    let cancelled = false;
    diaryService
      .list(
        {
          ...(propertyId ? { propertyId } : {}),
          ...(contactId ? { attendeeId: contactId } : {}),
          legalEntityId: legalEntityId ?? undefined,
          branchId: reachOutBranchId || undefined,
          startDate: toDiaryApiRangeStart(new Date()),
        },
        { page: 0, size: 10, sort: "startDate", direction: "asc" },
      )
      .then((res) => {
        if (cancelled) return;
        const upcoming = (res.content ?? []).find(
          (entry) => !entry.isCancelled && entry.startDate,
        );
        if (upcoming?.startDate) {
          setAppointmentLocal(toDatetimeLocalValue(upcoming.startDate));
          return;
        }
        setDefaultAppointment();
      })
      .catch(() => {
        if (!cancelled) setDefaultAppointment();
      });

    return () => {
      cancelled = true;
    };
  }, [
    templateNeedsAppointment,
    contactId,
    propertyId,
    legalEntityId,
    reachOutBranchId,
  ]);

  const reachoutContext = useMemo(
    () =>
      buildReachoutResolverContext({
        contact: selectedContact,
        propertySummary: selectedPropertySummary,
        propertyDetail: selectedPropertyDetail,
        legalEntityId,
        legalEntityName,
        branchId: reachOutBranchId,
        branchName: reachOutBranchName,
        appointmentLocal,
      }),
    [
      selectedContact,
      selectedPropertySummary,
      selectedPropertyDetail,
      legalEntityId,
      legalEntityName,
      reachOutBranchId,
      reachOutBranchName,
      appointmentLocal,
    ],
  );

  // Resize + pre-fill the params array whenever the picked template or resolver
  // context changes. Slots stay editable so the operator can override any value.
  useEffect(() => {
    if (!selectedTemplate) {
      setTemplateParams([]);
      return;
    }
    setTemplateParams(resolveTemplateParams(selectedTemplate, reachoutContext));
  }, [selectedTemplate, reachoutContext]);

  // Clear property if intent changes to RECOMMEND, or if the selected property is no longer valid for the new intent
  useEffect(() => {
    if (leadIntent === "AGENT_INIT_PROPERTY_RECOMMEND") {
      setPropertyId("");
    } else if (propertyId) {
      const prop = properties.find((p) => p.id === propertyId);
      if (prop) {
        const isRent =
          prop.market?.toLowerCase().includes("rent") ||
          prop.market?.toLowerCase().includes("let");
        const isSale = prop.market?.toLowerCase().includes("sale");
        if (leadIntent === "AGENT_INIT_PROPERTY_RENT" && !isRent)
          setPropertyId("");
        if (leadIntent === "AGENT_INIT_PROPERTY_SALES" && !isSale)
          setPropertyId("");
      }
    }
  }, [leadIntent, properties, propertyId]);

  // Custom mode disabled — template only for WhatsApp reach-out.
  // useEffect(() => {
  //   if (channelType !== "WHATSAPP" && messageMode === "template") {
  //     setMessageMode("custom");
  //   }
  // }, [channelType, messageMode]);

  const contactOptions = useMemo(
    () =>
      filterContactsByPhoneQuery(contacts, contactSearchQuery, contactId).map((c) => {
        const fromPerson = [c.forename, c.surname]
          .filter((p) => p?.trim())
          .join(" ")
          .trim();
        const label = c.name?.trim() || fromPerson || contactPrimaryLabel(c);
        const missingMobile = !c.mobilePhone?.trim();
        const subtitle =
          [c.mobilePhone?.trim(), c.email?.trim()].filter(Boolean).join(" · ") ||
          undefined;
        return {
          value: c.id,
          label,
          subtitle,
          disabled: missingMobile,
          disabledTooltip: missingMobile
            ? "This contact doesn't have a mobile phone number."
            : undefined,
        };
      }),
    [contacts, contactSearchQuery, contactId],
  );

  const requiresProperty =
    leadIntent === "AGENT_INIT_PROPERTY_RENT" ||
    leadIntent === "AGENT_INIT_PROPERTY_SALES";

  const propertyOptions = useMemo(() => {
    if (leadIntent === "AGENT_INIT_PROPERTY_RECOMMEND") {
      return requiresProperty ? [] : [{ value: "", label: "— No property anchor —" }];
    }

    const mapped = properties.map((p) => ({
      value: p.id,
      label: p.displayAddress || `Property ${p.id.slice(0, 8)}`,
      leading: <AvailabilityDot color={p.availabilityColor} />,
    }));

    if (requiresProperty) return mapped;

    return [{ value: "", label: "— No property anchor —" }, ...mapped];
  }, [properties, leadIntent, requiresProperty]);

  useEffect(() => {
    if (
      !selectedContact ||
      channelType === "EMAIL" ||
      !selectedContact.mobilePhone
    ) {
      setPhoneInfo(null);
      return;
    }
    const raw = selectedContact.mobilePhone;
    const normalized = normalizePhoneInput(raw);
    if (looksLikeE164(normalized)) {
      setPhoneInfo({
        hasCountryCode: true,
        formattedPhone: normalized,
        isValid: true,
      });
      setPhoneInfoLoading(false);
      return;
    }
    setPhoneInfoLoading(true);
    const override = normalized.startsWith("+")
      ? undefined
      : overrideCountryCode;
    reachoutService
      .formatPhone(raw, override)
      .then((info) => {
        const formatted = info.formattedPhone
          ? normalizePhoneInput(info.formattedPhone)
          : normalized.startsWith("+")
            ? normalized
            : `${overrideCountryCode}${normalized.replace(/^0+/, "")}`;
        const hasCountryCode =
          info.hasCountryCode || normalized.startsWith("+") || Boolean(override);
        setPhoneInfo({
          hasCountryCode,
          formattedPhone: formatted,
          isValid: info.isValid || (hasCountryCode && looksLikeE164(formatted)),
        });
      })
      .catch((err) => {
        console.error("Failed to format phone:", err);
        const fallback = normalized.startsWith("+")
          ? normalized
          : `${overrideCountryCode}${normalized.replace(/^0+/, "")}`;
        setPhoneInfo({
          hasCountryCode: normalized.startsWith("+") || !!overrideCountryCode,
          formattedPhone: fallback,
          isValid: looksLikeE164(fallback),
        });
      })
      .finally(() => setPhoneInfoLoading(false));
  }, [selectedContact, overrideCountryCode, channelType]);

  const hasRequiredMedia = useMemo(() => {
    if (!selectedContact) return false;
    return channelType === "EMAIL"
      ? !!selectedContact.email
      : !!selectedContact.mobilePhone;
  }, [selectedContact, channelType]);

  const templateModeReady = useMemo(() => {
    if (!selectedTemplate) return false;
    // Every {N} placeholder must have a non-empty value.
    const count = countTemplateParams(selectedTemplate.templateBody);
    for (let i = 0; i < count; i++) {
      if (!templateParams[i] || !templateParams[i].trim()) return false;
    }
    return true;
  }, [selectedTemplate, templateParams]);

  const livePreviewContactName = useMemo(() => {
    if (!selectedContact) return "";
    const fullName = [selectedContact.forename, selectedContact.surname]
      .filter(Boolean)
      .join(" ")
      .trim();
    return (
      selectedContact.name?.trim() || fullName || contactPrimaryLabel(selectedContact)
    );
  }, [selectedContact]);

  const livePreviewBody = useMemo(() => {
    if (messageMode !== "template") return "";
    if (!selectedTemplate) return "";
    return renderTemplatePreview(
      selectedTemplate.templateBody ?? "",
      templateParams,
    );
  }, [messageMode, selectedTemplate, templateParams]);

  const livePreviewImageUrl = useMemo(
    () =>
      resolveReachOutHeaderImageUrl({
        propertyId,
        propertyDetail: selectedPropertyDetail,
        propertySummary: selectedPropertySummary,
        previewImageUrlsByPropertyId,
        legalEntities: profileData?.legalEntities ?? [],
        branchId: reachOutBranchId,
        legalEntityId,
        branchSummaryLogoUrl: reachOutBranchLogoUrl,
      }),
    [
      propertyId,
      selectedPropertyDetail,
      selectedPropertySummary,
      previewImageUrlsByPropertyId,
      reachOutBranchId,
      reachOutBranchLogoUrl,
      legalEntityId,
      profileData?.legalEntities,
    ],
  );

  const reachOutPayloadImageUrl = useMemo(
    () =>
      resolveReachOutHeaderImageUrlForApi({
        propertyId,
        propertyDetail: selectedPropertyDetail,
        propertySummary: selectedPropertySummary,
        previewImageUrlsByPropertyId,
        legalEntities: profileData?.legalEntities ?? [],
        branchId: reachOutBranchId,
        legalEntityId,
        branchSummaryLogoUrl: reachOutBranchLogoUrl,
      }),
    [
      propertyId,
      selectedPropertyDetail,
      selectedPropertySummary,
      previewImageUrlsByPropertyId,
      reachOutBranchId,
      reachOutBranchLogoUrl,
      legalEntityId,
      profileData?.legalEntities,
    ],
  );

  const handleSubmit = async () => {
    setSubmitAttempted(true);

    if (!legalEntityId) return;
    if (!reachOutBranchId.trim()) return;
    if (requiresProperty && !propertyId.trim()) return;
    if (!contactId.trim()) return;
    if (messageMode === "template" && !selectedTemplateId) return;
    if (!selectedContact) return;
    if (selectedContact.archived) return;
    if (!hasRequiredMedia) return;
    if (messageMode === "template" && !templateModeReady) {
      showReachOutValidationError(
        "TEMPLATE_PARAMS_REQUIRED",
        "Fill in all template parameters.",
      );
      return;
    }
    if (channelType !== "EMAIL" && (phoneInfoLoading || !phoneInfo?.isValid)) return;

    const phone =
      channelType === "EMAIL"
        ? selectedContact.email
        : selectedContact.mobilePhone;
    if (!phone) {
      showReachOutValidationError(
        "CONTACT_PHONE_MISSING",
        channelType === "EMAIL"
          ? "Selected contact has no email on file."
          : "Selected contact has no mobile phone on file.",
      );
      return;
    }

    let finalPhone = phone;
    if (channelType !== "EMAIL") {
      finalPhone = phoneInfo?.formattedPhone || phone;
    }

    // Build the {messages:[...]} entry — template mode only (custom disabled).
    let message: ReachoutMessage;
    if (messageMode === "template" && selectedTemplate) {
      message = {
        messageType: "template",
        // Atlas/flow only needs the template name + params + imageUrl.
        // The rendered body is reconstructed downstream from WhatsApp Business API.
        content: renderTemplatePreview(selectedTemplate.templateBody ?? "", templateParams),
        templateName: selectedTemplate.templateName,
        templateParams: templateParams,
        imageUrl: propertyId ? reachOutPayloadImageUrl : null,
      } as ReachoutMessage;
    } else {
      return;
    }
    // Custom text mode disabled:
    // } else {
    //   message = { messageType: "text", content };
    // }

    setSubmitting(true);
    try {
      // FlowReachoutMessageIn carries phone + email as separate fields, and
      // branchId is singular. Atlas resolves channelId and nulls
      // conversationId / metadata server-side. contactId comes straight from
      // the selected contact — phone-format drift made the server-side
      // fuzzy lookup unreliable (e.g. "447…" vs "+447…").
      await reachoutService.publish({
        channelType,
        contactId: selectedContact.id ?? null,
        personPhoneNumber: channelType !== "EMAIL" ? finalPhone : null,
        personEmailAddress: channelType === "EMAIL" ? finalPhone : null,
        legalEntityId,
        branchId: reachOutBranchId || null,
        leadIntent,
        propertyId: propertyId || null,
        messages: [message],
        timestamp: new Date().toISOString(),
      });
      setTemplateParams([]);
      showActionSuccess(
        "Reach-out queued. The downstream flow will pick it up shortly.",
        { title: "Reach-out published" },
      );
    } catch (err: unknown) {
      showActionErrorFromUnknown(err, "Failed to publish reach-out.", {
        title: "Could not publish reach-out",
        code: (err as ApiError)?.code ?? "PUBLISH_FAILED",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-bridge-bg overflow-hidden">
      <header className="px-6 pt-5 pb-3 w-full border-b border-bridge-border/60 bg-white">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-bridge-accent text-white shrink-0">
              <Send className="w-4 h-4 -ml-0.5" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-bridge-fg leading-tight">
                Reach Out
              </h1>
              <p className="text-xs text-bridge-muted">
                Send a templated message to a contact about a property.
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-6">
        <div className="max-w-5xl mx-auto space-y-4">
          {!legalEntityId && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-center gap-3 text-amber-900 text-sm">
              <AlertCircle className="w-5 h-5 shrink-0 text-amber-600" />
              Select a Legal Entity from the top of the page first.
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-6 items-start">
            <div className="min-h-0">
              <div className="bg-white rounded-xl shadow-sm p-6 space-y-5 border border-bridge-border/70 h-full">
                <section className="space-y-2">
                  <span className="block text-xs font-semibold text-bridge-muted uppercase tracking-wider">
                    Channel
                  </span>
                  <ReachOutChannelToggle
                    value={channelType}
                    onChange={setChannelType}
                    disabled={!legalEntityId}
                  />
                </section>

                <section className="space-y-1">
                  <div className="mb-1.5">
                    <span className="inline-flex items-center gap-2 text-xs font-semibold text-bridge-muted uppercase tracking-wider">
                      <span>
                        Branch <span className="text-red-500">*</span>
                      </span>
                      <Tooltip
                        side="bottom-right"
                        variant="muted"
                        content="The branch this reach-out is sent from — sets which channel and sender are used. Changing it updates the contacts, properties and templates you can choose in the fields below."
                      >
                        <button
                          type="button"
                          aria-label="About the branch field"
                          className="inline-flex"
                        >
                          <span className="inline-flex h-2.5 w-2.5 items-center justify-center rounded-full bg-bridge-muted text-white text-[7px] font-serif italic leading-none transition-colors hover:bg-bridge-fg">
                            i
                          </span>
                        </button>
                      </Tooltip>
                    </span>
                  </div>
                  <BranchScopeSelect
                    value={reachOutBranchId}
                    onChange={(id, branch) => {
                      branchManuallySelectedRef.current = true;
                      setReachOutBranchId(id);
                      setReachOutBranchName(branch?.name ?? "");
                      setReachOutBranchLogoUrl(branch?.logoUrl ?? undefined);
                    }}
                    placeholder="Select branch"
                    disabled={!legalEntityId}
                    invalid={submitAttempted && !reachOutBranchId.trim()}
                  />
                  {submitAttempted && !reachOutBranchId.trim() && (
                    <div className="text-xs text-red-500 flex items-center gap-1.5 mt-1 font-medium animate-in fade-in slide-in-from-top-1 duration-200">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                      <span>Select a branch.</span>
                    </div>
                  )}
                  {showNoCommunicationChannelMessage && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex gap-2.5 text-amber-900 text-sm mt-2">
                      <AlertCircle className="w-4 h-4 shrink-0 text-amber-600 mt-0.5" />
                      <p>
                        {`${reachOutBranchName || legalEntityName || "This branch"} has no active WhatsApp channel at branch, parent, or root legal entity level. Configure one in Integrations → Communication Channels.`}
                      </p>
                    </div>
                  )}
                </section>

                <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <MenuSelect
                    label="Lead Intent"
                    required
                    options={LEAD_INTENT_OPTIONS}
                    value={leadIntent}
                    onChange={(v) => setLeadIntent(v as LeadIntent)}
                    placeholder="Select intent"
                  />

                  <div>
                    <MenuSelect
                      label="Related Property"
                      required={requiresProperty}
                      options={propertyOptions}
                      value={propertyId}
                      onChange={setPropertyId}
                      placeholder={
                        leadIntent === "AGENT_INIT_PROPERTY_RECOMMEND"
                          ? "Not required for recommendations"
                          : requiresProperty && propertyOptions.length === 0
                            ? "No matching properties available"
                            : "Select property"
                      }
                      disabled={leadIntent === "AGENT_INIT_PROPERTY_RECOMMEND"}
                      onDropdownSearchQueryChange={setPropertySearchQuery}
                      onDropdownEndReached={handlePropertyEndReached}
                      loading={loadingProperties}
                      hasMorePages={propertiesHasNext}
                      searchable
                    />
                    {submitAttempted && requiresProperty && !propertyId.trim() && (
                      <div className="text-xs text-red-500 flex items-center gap-1.5 mt-1 font-medium animate-in fade-in slide-in-from-top-1 duration-200">
                        <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                        <span>Select a related property.</span>
                      </div>
                    )}
                  </div>
                </section>

                <section className="space-y-1">
                  <div className="flex items-end gap-3">
                    <div className="flex-1 min-w-0">
                      <MenuSelect
                        label="Contact"
                        required
                        options={contactOptions}
                        value={contactId}
                        onChange={setContactId}
                        placeholder={
                          contactOptions.length === 0 && !contactSearchQuery
                            ? "No contacts available"
                            : "Select contact"
                        }
                        disabled={!legalEntityId}
                        onDropdownSearchQueryChange={setContactSearchQuery}
                        onDropdownEndReached={handleContactEndReached}
                        loading={loadingContacts}
                        hasMorePages={contactsHasNext}
                        searchable
                      />
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      className="h-[42px] px-4 rounded-xl border border-slate-200 bg-white text-bridge-fg hover:bg-slate-50 shrink-0"
                      disabled={!legalEntityId}
                      onClick={() => setIsCreateContactModalOpen(true)}
                    >
                      + Create
                    </Button>
                  </div>
                  {submitAttempted && !contactId && (
                    <div className="text-xs text-red-500 flex items-center gap-1.5 mt-1 font-medium animate-in fade-in slide-in-from-top-1 duration-200">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                      <span>Select a contact.</span>
                    </div>
                  )}
                  {contactId && selectedContact?.archived && (
                    <div className="text-xs text-red-500 flex items-center gap-1.5 mt-1 font-medium animate-in fade-in slide-in-from-top-1 duration-200">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                      <span>
                        Contact is archived. First unarchive it from contacts
                        section.
                      </span>
                    </div>
                  )}
                  {contactId &&
                    !selectedContact?.archived &&
                    !hasRequiredMedia && (
                      <div className="text-xs text-red-500 flex items-center gap-1.5 mt-1 font-medium animate-in fade-in slide-in-from-top-1 duration-200">
                        <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                        <span>
                          {channelType === "EMAIL"
                            ? "Selected contact has no email address on file."
                            : "Selected contact has no mobile phone number on file."}
                        </span>
                      </div>
                    )}
                  {contactId &&
                    !selectedContact?.archived &&
                    selectedContact?.mobilePhone &&
                    channelType !== "EMAIL" && (
                      <div className="mt-3">
                        {phoneInfoLoading ? (
                          <div className="text-xs text-slate-400 flex items-center gap-2">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Validating phone number...
                          </div>
                        ) : phoneInfo && !phoneInfo.hasCountryCode ? (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl space-y-2 animate-in fade-in slide-in-from-top-1">
                            <div className="text-xs font-medium text-amber-800 flex items-center gap-1.5">
                              <AlertCircle className="w-3.5 h-3.5" />
                              Phone number is missing a country code. Select one
                              to proceed:
                            </div>
                            <div className="flex gap-2">
                              <div className="w-1/3 min-w-[120px]">
                                <MenuSelect
                                  options={phoneCodeOptions}
                                  value={toCompositeCode(overrideCountryCode)}
                                  onChange={(v) => setOverrideCountryCode(toDialCode(v))}
                                  placeholder="Code"
                                  loading={loadingPhoneCodes}
                                  searchable
                                />
                              </div>
                              <div className="flex-1 px-3 py-2 bg-white/60 border border-amber-200 rounded-lg text-sm text-amber-900/70 font-mono flex items-center shadow-sm">
                                {selectedContact.mobilePhone}
                              </div>
                            </div>
                            {!phoneInfo.isValid && (
                              <div className="text-xs text-red-500 font-medium">
                                Number is invalid even with this country code.
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-1.5">
                            <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono text-slate-600 flex items-center gap-2">
                              <span className="font-semibold text-slate-400">
                                Phone:
                              </span>
                              {phoneInfo?.formattedPhone ||
                                selectedContact.mobilePhone}
                            </div>
                            {phoneInfo && !phoneInfo.isValid && (
                              <div className="text-xs text-red-500 font-medium flex items-center gap-1.5">
                                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                                Number format is invalid (E.164 expected).
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                </section>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-semibold text-bridge-fg">
                      Message
                    </label>
                    <div
                      className="inline-flex rounded-xl border border-bridge-border bg-bridge-bg p-1 text-xs font-bold"
                      role="tablist"
                      aria-label="Message mode"
                    >
                      {/* Custom plain-text mode disabled
                  <button
                    type="button"
                    onClick={() => setMessageMode("custom")}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors ${
                      messageMode === "custom"
                        ? "bg-white text-bridge-fg shadow-sm"
                        : "text-bridge-muted hover:text-bridge-fg"
                    }`}
                  >
                    <PenLine className="w-3.5 h-3.5" /> Custom
                  </button>
                  */}
                      <button
                        type="button"
                        onClick={() => setMessageMode("template")}
                        disabled={channelType !== "WHATSAPP"}
                        title={
                          channelType === "WHATSAPP"
                            ? "Send a pre-approved WhatsApp template"
                            : "Templates are only available for WhatsApp"
                        }
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${messageMode === "template"
                            ? "bg-white text-bridge-fg shadow-sm"
                            : "text-bridge-muted hover:text-bridge-fg"
                          }`}
                      >
                        <FileText className="w-3.5 h-3.5" /> Template
                      </button>
                    </div>
                  </div>

                  {/* Custom plain-text mode disabled
              {messageMode === "custom" ? (
                <>
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Type the message body that will be sent to the contact…"
                    rows={5}
                    className="w-full px-4 py-3 bg-bridge-bg border border-bridge-border rounded-xl text-sm text-bridge-fg resize-y focus:ring-2 focus:ring-bridge-accent focus:border-bridge-accent outline-none"
                  />
                  <p className="text-[10px] text-slate-400">
                    Sent as <code className="font-mono">messageType: "text"</code> on the envelope.
                  </p>
                </>
              ) : (
              */}
                  {messageMode === "template" && (
                    <div className="space-y-3">
                      <MenuSelect
                        label="WhatsApp Template"
                        required
                        options={
                          templatesLoading
                            ? [{ value: "", label: "Loading templates…" }]
                            : templates.length === 0
                              ? [
                                {
                                  value: "",
                                  label: "No approved templates for this scope",
                                },
                              ]
                              : templates.map((t) => ({
                                value: t.id,
                                label: `${t.templateName} · ${t.language}${t.waStatus ? ` · ${t.waStatus}` : ""}`,
                              }))
                        }
                        value={selectedTemplateId}
                        onChange={setSelectedTemplateId}
                        placeholder="Select template"
                        disabled={templatesLoading || templates.length === 0}
                        searchable
                      />
                      {submitAttempted && !selectedTemplateId && (
                        <div className="text-xs text-red-500 flex items-center gap-1.5 mt-1 font-medium animate-in fade-in slide-in-from-top-1 duration-200">
                          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                          <span>Select a WhatsApp template.</span>
                        </div>
                      )}

                      {selectedTemplate && (
                        <>
                          <div className="p-3 bg-bridge-bg/50 border border-bridge-border rounded-xl text-sm text-bridge-fg whitespace-pre-wrap">
                            {selectedTemplate.templateBody || (
                              <span className="text-bridge-muted italic">
                                No body on this template.
                              </span>
                            )}
                            {selectedTemplate.footer && (
                              <div className="mt-2 pt-2 border-t border-bridge-border/50 text-xs text-bridge-muted">
                                {selectedTemplate.footer}
                              </div>
                            )}
                          </div>

                          {templateNeedsAppointment && (
                            <div className="space-y-1.5">
                              <label className="text-[10px] uppercase tracking-wider font-bold text-bridge-muted">
                                Appointment date &amp; time
                              </label>
                              <input
                                type="datetime-local"
                                value={appointmentLocal}
                                onChange={(e) => setAppointmentLocal(e.target.value)}
                                className="w-full px-3 py-2 bg-bridge-bg border border-bridge-border rounded-lg text-sm text-bridge-fg focus:ring-2 focus:ring-bridge-accent focus:border-bridge-accent outline-none"
                              />
                              <p className="text-[10px] text-slate-400">
                                Used for template placeholders mapped to{" "}
                                <code className="font-mono">appointment.time</code>.
                                Prefilled from the next matching diary entry when available.
                              </p>
                            </div>
                          )}

                          {templateParams.length > 0 && (
                            <div className="space-y-2">
                              <div className="text-[10px] uppercase tracking-wider font-bold text-bridge-muted">
                                Template parameters
                              </div>
                              {templateParams.map((value, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                  <span className="text-xs font-mono text-bridge-muted w-8">{`{${idx + 1}}`}</span>
                                  <input
                                    type="text"
                                    value={value}
                                    onChange={(e) =>
                                      setTemplateParams((prev) => {
                                        const next = [...prev];
                                        next[idx] = e.target.value;
                                        return next;
                                      })
                                    }
                                    placeholder={`Value for {${idx + 1}}`}
                                    className="flex-1 px-3 py-2 bg-bridge-bg border border-bridge-border rounded-lg text-sm text-bridge-fg focus:ring-2 focus:ring-bridge-accent focus:border-bridge-accent outline-none"
                                  />
                                </div>
                              ))}
                            </div>
                          )}

                          <p className="text-[10px] text-slate-400">
                            Sent as{" "}
                            <code className="font-mono">
                              messageType: "template"
                            </code>{" "}
                            with <code className="font-mono">templateName</code> +{" "}
                            <code className="font-mono">templateParams</code> on the
                            envelope.
                          </p>
                        </>
                      )}
                    </div>
                  )}
                  {/* )} end custom/template ternary */}
                </div>

                <div className="pt-1">
                  <Button
                    variant="primary"
                    className="w-full h-11 rounded-xl font-semibold text-sm disabled:opacity-50"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send size={18} />
                        Publish Reach-Out
                      </span>
                    )}
                  </Button>
                </div>
              </div>

            </div>

            <aside className="min-h-0 flex flex-col items-center pt-1 w-full max-w-[280px] mx-auto lg:sticky lg:top-4">
              <div className="text-[10px] font-semibold text-bridge-muted uppercase tracking-wider mb-3">
                Live Preview
              </div>
              <WhatsappLivePreview
                contactName={livePreviewContactName}
                statusLabel={reachOutBranchName || legalEntityName || "Reachout"}
                messageBody={
                  selectedTemplate
                    ? livePreviewBody || selectedTemplate.templateBody || ""
                    : null
                }
                footer={selectedTemplate?.footer}
                headerImageUrl={livePreviewImageUrl}
                headerImageAlt={
                  selectedPropertyDetail?.displayAddress?.trim() ||
                  selectedPropertySummary?.displayAddress?.trim() ||
                  reachOutBranchName ||
                  legalEntityName ||
                  "Agency logo"
                }
              />
            </aside>
          </div>
        </div>
      </main>

      {isCreateContactModalOpen && legalEntityId && (
        <ReachOutContactCreateModal
          legalEntityId={legalEntityId}
          reachOutBranchId={reachOutBranchId || undefined}
          onClose={() => setIsCreateContactModalOpen(false)}
          onCreated={(newContact, createdBranchId) => {
            setIsCreateContactModalOpen(false);
            if (reachOutBranchId && createdBranchId !== reachOutBranchId) {
              return;
            }
            setContacts((prev) => [newContact, ...prev]);
            setContactId(newContact.id);
          }}
        />
      )}
    </div>
  );
};
